import React, { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Zap, MapPin, Clock, CheckCircle, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function BusinessBoostPage() {
  const [searchParams] = useSearchParams();
  const [pricing, setPricing] = useState(null);
  const [boosts, setBoosts] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRadius, setSelectedRadius] = useState(10);
  const [selectedDuration, setSelectedDuration] = useState(7);
  const [purchasing, setPurchasing] = useState(false);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    fetchData();
    if (sessionId) verifyBoost();
  }, []);

  const fetchData = async () => {
    try {
      const [pricingRes, boostsRes] = await Promise.all([
        fetch(`${API}/business/boost/pricing`, { headers }),
        fetch(`${API}/business/boosts`, { headers })
      ]);
      if (pricingRes.ok) setPricing(await pricingRes.json());
      if (boostsRes.ok) setBoosts(await boostsRes.json());
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  const verifyBoost = async () => {
    try {
      const res = await fetch(`${API}/business/boost/verify/${sessionId}`, { headers });
      const data = await res.json();
      if (data.status === 'paid') {
        toast.success(data.message || 'Boost active !');
        fetchData();
      }
    } catch {}
    window.history.replaceState({}, '', '/business/boost');
  };

  const handleCheckout = async () => {
    setPurchasing(true);
    try {
      const formData = new FormData();
      formData.append('radius_km', selectedRadius);
      formData.append('duration_days', selectedDuration);
      formData.append('origin_url', window.location.origin);
      const res = await fetch(`${API}/business/boost/checkout`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      const data = await res.json();
      if (data.url) window.location.href = data.url;
      else toast.error(data.detail || 'Erreur');
    } catch { toast.error('Erreur reseau'); }
    finally { setPurchasing(false); }
  };

  const getPrice = () => selectedRadius * selectedDuration * (pricing?.price_per_day_per_km || 1);

  const radiusOptions = [5, 10, 20, 50];
  const durationOptions = [
    { days: 7, label: '1 semaine' },
    { days: 14, label: '2 semaines' },
    { days: 30, label: '1 mois' }
  ];

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><div className="animate-spin w-8 h-8 border-4 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div className="min-h-screen bg-gray-50 pb-24">
      <div className="bg-white border-b sticky top-16 z-30">
        <div className="max-w-4xl mx-auto px-4 py-3 flex items-center gap-3">
          <Link to="/business/dashboard"><ArrowLeft className="w-5 h-5 text-charcoal" /></Link>
          <h1 className="font-bold text-charcoal text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-amber-500" /> Mise en avant
          </h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Active Boost */}
        {boosts?.active_boost && (
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-5" data-testid="active-boost-banner">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-amber-500 rounded-full flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-charcoal">Boost actif !</h3>
                <p className="text-sm text-muted-foreground">
                  Rayon de {boosts.active_boost.radius_km} km &bull; Jusqu'au {new Date(boosts.active_boost.ends_at).toLocaleDateString('fr-FR')}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Explanation */}
        <div className="bg-white rounded-xl p-5 shadow-sm">
          <h2 className="font-semibold text-charcoal mb-3">Comment ca marche ?</h2>
          <div className="space-y-3">
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <MapPin className="w-4 h-4 text-primary" />
              </div>
              <div>
                <p className="font-medium text-charcoal text-sm">Visibilite elargie</p>
                <p className="text-sm text-muted-foreground">Votre commerce apparait en priorite dans l'annuaire et sur la carte pour tous les utilisateurs dans le rayon choisi.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <Zap className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-charcoal text-sm">Badge "Mise en avant"</p>
                <p className="text-sm text-muted-foreground">Un badge distinctif attire l'attention des utilisateurs sur votre profil.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-charcoal text-sm">Tarif simple : 1 EUR/jour/km</p>
                <p className="text-sm text-muted-foreground">Choisissez votre rayon et la duree. Sans engagement, paiement unique.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Configuration */}
        <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="boost-config">
          <h2 className="font-semibold text-charcoal mb-4">Configurer votre boost</h2>
          
          {/* Radius */}
          <div className="mb-5">
            <label className="text-sm font-medium text-charcoal mb-2 block">Rayon de visibilite</label>
            <div className="grid grid-cols-4 gap-2">
              {radiusOptions.map(r => (
                <button key={r} onClick={() => setSelectedRadius(r)}
                  className={`py-3 rounded-xl text-center font-medium text-sm border-2 transition-all ${
                    selectedRadius === r
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 text-charcoal hover:border-gray-300'
                  }`}
                  data-testid={`boost-radius-${r}`}>
                  {r} km
                </button>
              ))}
            </div>
          </div>

          {/* Duration */}
          <div className="mb-5">
            <label className="text-sm font-medium text-charcoal mb-2 block">Duree</label>
            <div className="grid grid-cols-3 gap-2">
              {durationOptions.map(d => (
                <button key={d.days} onClick={() => setSelectedDuration(d.days)}
                  className={`py-3 rounded-xl text-center font-medium text-sm border-2 transition-all ${
                    selectedDuration === d.days
                      ? 'border-primary bg-primary/5 text-primary'
                      : 'border-gray-200 text-charcoal hover:border-gray-300'
                  }`}
                  data-testid={`boost-duration-${d.days}`}>
                  {d.label}
                </button>
              ))}
            </div>
          </div>

          {/* Price Summary */}
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">Rayon</span>
              <span className="font-medium text-charcoal">{selectedRadius} km</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">Duree</span>
              <span className="font-medium text-charcoal">{selectedDuration} jours</span>
            </div>
            <div className="flex justify-between items-center mb-2">
              <span className="text-sm text-muted-foreground">Tarif</span>
              <span className="text-sm text-muted-foreground">{selectedRadius} km x {selectedDuration} j x 1 EUR</span>
            </div>
            <div className="border-t pt-2 mt-2 flex justify-between items-center">
              <span className="font-semibold text-charcoal">Total</span>
              <span className="text-2xl font-bold text-charcoal">{getPrice()} EUR</span>
            </div>
          </div>

          <Button className="w-full" size="lg" onClick={handleCheckout} disabled={purchasing} data-testid="boost-checkout-btn">
            {purchasing ? (
              <span className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Activer la mise en avant - {getPrice()} EUR
          </Button>
        </div>

        {/* Boost History */}
        {boosts?.boosts?.length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm">
            <h2 className="font-semibold text-charcoal mb-3">Historique</h2>
            <div className="space-y-2">
              {boosts.boosts.map(b => (
                <div key={b.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg text-sm">
                  <div>
                    <span className="font-medium text-charcoal">{b.radius_km} km / {b.duration_days}j</span>
                    <p className="text-xs text-muted-foreground">
                      {new Date(b.starts_at).toLocaleDateString('fr-FR')} - {new Date(b.ends_at).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="font-medium text-charcoal">{b.amount_paid} EUR</span>
                    {b.ends_at > new Date().toISOString() ? (
                      <p className="text-xs text-green-600">Actif</p>
                    ) : (
                      <p className="text-xs text-muted-foreground">Expire</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
